import type {
	PersonalizedTemplatesV2AllTemplatesQuery,
	PersonalizedTemplatesV2AllTemplatesQuery_allTemplates_PaginatedTemplateInfoList_nodes_TemplateInfo as TemplateInfoType,
} from '../__types__/PersonalizedTemplatesV2AllTemplatesQuery.js';

export enum BLUEPRINT_NAMES {
	NINETY_DAY_PLAN = '90-day plan',
	ALL_HANDS_MEETING = 'All hands meeting',
	ANNUAL_PLAN_ONE_PAGER = 'Annual plan one-pager',
	EXECUTIVE_BUSINESS_REVIEW = 'Executive business review',
	BUSINESS_STATUS_UPDATE = 'Business status update',
	BUSINESS_PLAN_ONE_PAGER = 'Business plan one-pager',
	CAREER_DEVELOPMENT_PLAN = 'Career development plan',
	COMPETITIVE_ANALYSIS = 'Competitive analysis',
	CONTENT_DESIGN_GLOSSARY = 'Content design glossary',
	CONTENT_STRATEGY = 'Content strategy',
	CUSTOMER_IMPACT_ASSESSMENT = 'Customer Impact Assessment',
	CUSTOMER_INTERVIEW_REPORT = 'Customer Interview Report',
	DACI_DECISION_DOCUMENTATION = 'DACI: Decision documentation',
	DESIGN_DECISION = 'Design decision',
	DESIGN_REVIEW = 'Design review',
	DESIGN_SPRINT = 'Design sprint',
	DESIGN_SYSTEMS = 'Design systems',
	DEVOPS_RUNBOOK = 'DevOps runbook',
	EMPLOYEE_HANDBOOK = 'Employee handbook',
	HR_KNOWLEDGE_BASE_MANAGEMENT = 'HR Knowledge Base Management',
	HOW_TO_ARTICLE = 'How-to article',
	ITSM_KNOWN_ERRORS = 'ITSM Known Errors',
	ITSM_CHANGE_MANAGEMENT = 'ITSM change management',
	ITSM_POST_INCIDENT_REVIEWS = 'ITSM post incident reviews',
	ITSM_RUNBOOK = 'ITSM runbook',
	INCIDENT_COMMUNICATION = 'Incident communication',
	INCIDENT_POSTMORTEM = 'Incident postmortem',
	JOB_DESCRIPTION = 'Job description',
	LENNYS_PERFORMANCE_REVIEW = "Lenny's Performance Review",
	LENNYS_PRODUCT_REQUIREMENTS = "Lenny's product requirements",
	MARKETING_PLAN = 'Marketing plan',
	MEETING_NOTES = 'Meeting notes',
	MESSAGE_HOUSE = 'Message house',
	OKRS = 'OKRs',
	PERSONA = 'Persona',
	PRESENTER_MODE = 'Presenter mode',
	PRODUCT_REQUIREMENTS = 'Product Requirements',
	PRODUCT_ROADMAP = 'Product roadmap',
	PROJECT_KICKOFF = 'Project Kickoff',
	PROJECT_PLAN = 'Project plan',
	PROJECT_STATUS = 'Project status',
	PROJECT_TIMELINE_TEMPLATE = 'Project Timeline Template',
	QTC_ACQUISITION = 'QTC Acquisition',
	QUARTERLY_CHECK_IN = 'Quarterly check in',
	REMOTE_TEAM_MEETING = 'Remote team meeting',
	RISK_ASSESSMENT_MATRIX = 'Risk assessment matrix',
	ROLES_AND_RESPONSIBILITIES = 'Roles and responsibilities play',
	SMART_GOALS = 'SMART Goals',
	SWOT_ANALYSIS = 'SWOT analysis',
	SALES_PORTAL = 'Sales Portal',
	SALES_ACCOUNT_PLANNING = 'Sales account planning',
	SERVICE_TEAM_HEALTH_MONITOR = 'Service team health monitor',
	SOFTWARE_ARCHITECTURE_REVIEW = 'Software Architecture Review',
	SPRINT_PLANNING_MEETING = 'Sprint planning meeting',
	STAKEHOLDER_COMMUNICATIONS = 'Stakeholder communications',
	STANDARD_OPERATING_PROCEDURE_TEMPLATE = 'Standard Operating Procedure (SOP) Template',
	STRATEGIC_PLAN = 'Strategic plan',
	TEAM_HOMEPAGE = 'Team homepage',
	TEAM_POSTER = 'Team poster',
	TO_DO_LIST = 'To-do list',
	TROUBLESHOOTING_ARTICLE = 'Troubleshooting article',
	VENDOR_AGREEMENT = 'Vendor agreement',
	VIRTUAL_EVENT = 'Virtual event',
	VISION_TO_VALUES = 'Vision to values',
	END_OF_WEEK_STATUS_REPORT = 'End of week status report',
	WEEKLY_MEETING_NOTES = 'Weekly meeting notes',
	WORK_TRIP_PLANNER = 'Work trip planner',
	WORKSHOP_PLANNER = 'Workshop planner',
	WRITING_GUIDELINES = 'Writing guidelines',
	AWS_ARCHITECTURE_DIAGRAM = 'AWS architecture diagram',
	MARKETING_BLOG_POST = 'Marketing blog post',
	MARKETING_CAMPAIGN = 'Marketing campaign',
	HIRING_PROCESS = 'Hiring process',
	MUTUAL_ACTION_PLAN = 'Mutual action plan',
	ITSM_WEEKLY_MAJOR_INCIDENT_REPORT = 'ITSM weekly major incident report',
	DISRUPTIVE_BRAINSTORMING = 'Disruptive brainstorming',
}

export const BLUEPRINT_NAMES_SET = new Set(Object.values(BLUEPRINT_NAMES));
export interface BlueprintMapping {
	blueprintModuleCompleteKey: string;
	itemModuleCompleteKey: string;
	contentBlueprintId: string;
}

export const BLUEPRINT_NAME_TO_IDS_MAPPING: Partial<Record<BLUEPRINT_NAMES, BlueprintMapping>> = {
	[BLUEPRINT_NAMES.NINETY_DAY_PLAN]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:90-day-plan-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:90-day-plan-item',
		contentBlueprintId: 'b18bd4b0-cb12-4c8a-be83-bb5769b25481',
	},
	[BLUEPRINT_NAMES.ALL_HANDS_MEETING]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:all-hands-meeting-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:all-hands-meeting-item',
		contentBlueprintId: '67dddaa6-f038-45bf-b21f-8259dcf441db',
	},
	[BLUEPRINT_NAMES.ANNUAL_PLAN_ONE_PAGER]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:annual-plan-one-pager-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:annual-plan-one-pager-item',
		contentBlueprintId: '2823fda6-dfb4-4b81-8394-2e19e96b99fb',
	},
	[BLUEPRINT_NAMES.EXECUTIVE_BUSINESS_REVIEW]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:executive-business-review-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:executive-business-review-item',
		contentBlueprintId: '9433bfd2-c7b2-41cf-b493-d4a1feb39210',
	},
	[BLUEPRINT_NAMES.BUSINESS_STATUS_UPDATE]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:business-status-update-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:business-status-update-item',
		contentBlueprintId: 'f877653e-87e1-4654-a915-3591b3dd0db3',
	},
	[BLUEPRINT_NAMES.BUSINESS_PLAN_ONE_PAGER]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:business-plan-one-pager-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:business-plan-one-pager-item',
		contentBlueprintId: '6db15ae0-081f-4a9b-9936-15dec72492ee',
	},
	[BLUEPRINT_NAMES.CAREER_DEVELOPMENT_PLAN]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:career-development-plan-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:career-development-plan-item',
		contentBlueprintId: '2b75697e-04cd-4e68-8eec-c3828a0d8c36',
	},
	[BLUEPRINT_NAMES.COMPETITIVE_ANALYSIS]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:competitive-analysis-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:competitive-analysis-item',
		contentBlueprintId: '1f0e6e6e-766b-4e98-9022-90ab4ef6194f',
	},
	[BLUEPRINT_NAMES.CONTENT_DESIGN_GLOSSARY]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:content-design-glossary-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:content-design-glossary-item',
		contentBlueprintId: 'bc251e1b-c784-4593-a360-d5a5bbccd56b',
	},
	[BLUEPRINT_NAMES.CONTENT_STRATEGY]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:content-strategy-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:content-strategy-item',
		contentBlueprintId: '30735803-4a23-40df-9848-1ab97067e66c',
	},
	[BLUEPRINT_NAMES.CUSTOMER_IMPACT_ASSESSMENT]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:customer-impact-assessment-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:customer-impact-assessment-item',
		contentBlueprintId: '71a66b1f-49c9-4f9e-87fa-895850e1d2db',
	},
	[BLUEPRINT_NAMES.CUSTOMER_INTERVIEW_REPORT]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:customer-interview-report-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:customer-interview-report-item',
		contentBlueprintId: '5322e0f4-2f85-45f3-8ffe-94fba1d571d8',
	},
	[BLUEPRINT_NAMES.DACI_DECISION_DOCUMENTATION]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:decisions-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:decisions-blueprint-item',
		contentBlueprintId: '7edaef4a-329f-47c9-abfe-7e241c9e17ba',
	},
	[BLUEPRINT_NAMES.DESIGN_DECISION]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:design-decision-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:design-decision-item',
		contentBlueprintId: 'fb9933f8-841d-43ea-a872-ef06abd7f725',
	},
	[BLUEPRINT_NAMES.DESIGN_REVIEW]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:design-review-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:design-review-item',
		contentBlueprintId: '22f59064-c993-4fa9-bfd7-6a9e1be0ba4c',
	},
	[BLUEPRINT_NAMES.DESIGN_SPRINT]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:design-sprint-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:design-sprint-item',
		contentBlueprintId: '3acf77bc-919c-4bd5-a8ee-cf1f045d01d5',
	},
	[BLUEPRINT_NAMES.DESIGN_SYSTEMS]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:design-systems-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:design-systems-item',
		contentBlueprintId: '337e0487-2a9c-4ce9-95b0-4212c228379a',
	},
	[BLUEPRINT_NAMES.DEVOPS_RUNBOOK]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:devops-runbook-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:devops-runbook-item',
		contentBlueprintId: 'eb916d7d-a90d-4d18-955b-445e28ce08a5',
	},
	[BLUEPRINT_NAMES.EMPLOYEE_HANDBOOK]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:employee-handbook-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:employee-handbook-item',
		contentBlueprintId: '705c6810-8621-4c26-8d4e-ec51c92fd395',
	},
	[BLUEPRINT_NAMES.HR_KNOWLEDGE_BASE_MANAGEMENT]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:hr-knowledge-base-management-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:hr-knowledge-base-management-item',
		contentBlueprintId: '62eb020a-ab61-4a64-8f96-788745e1761c',
	},
	[BLUEPRINT_NAMES.HOW_TO_ARTICLE]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-knowledge-base:kb-how-to-article-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-knowledge-base:kb-how-to-item',
		contentBlueprintId: '74323bcf-829b-4d71-a765-facc886accdd',
	},
	[BLUEPRINT_NAMES.ITSM_KNOWN_ERRORS]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:itsm-known-errors-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:itsm-known-errors-item',
		contentBlueprintId: '55c00b7d-e8e9-4644-ae6c-08bc4a480b70',
	},
	[BLUEPRINT_NAMES.ITSM_CHANGE_MANAGEMENT]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:itsm-change-management-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:itsm-change-management-item',
		contentBlueprintId: '0ad3bc27-3852-4166-af6b-d5ffe48060e9',
	},
	[BLUEPRINT_NAMES.ITSM_POST_INCIDENT_REVIEWS]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:itsm-pir-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:itsm-pir-item',
		contentBlueprintId: '0d50396d-ca50-4c57-90aa-91dea817d4db',
	},
	[BLUEPRINT_NAMES.ITSM_RUNBOOK]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:itsm-runbook-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:itsm-runbook-item',
		contentBlueprintId: '9866346f-4709-4ae3-ba43-05bd2c30f689',
	},
	[BLUEPRINT_NAMES.INCIDENT_COMMUNICATION]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:incident-communication-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:incident-communication-item',
		contentBlueprintId: '45175536-78b0-4f59-a44c-47b0994037d0',
	},
	[BLUEPRINT_NAMES.INCIDENT_POSTMORTEM]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:incident-management-post-mortem-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:incident-management-post-mortem-item',
		contentBlueprintId: '08117408-2841-483d-a2f6-7fe1c8985b8d',
	},
	[BLUEPRINT_NAMES.JOB_DESCRIPTION]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:job-description-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:job-description-item',
		contentBlueprintId: '2571ed8f-b86b-4f30-aadd-997bbb259906',
	},
	[BLUEPRINT_NAMES.LENNYS_PERFORMANCE_REVIEW]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:lennys-performance-review-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:lennys-performance-review-item',
		contentBlueprintId: '8c187053-1bc6-4fef-a9f1-3d22e02b94b4',
	},
	[BLUEPRINT_NAMES.LENNYS_PRODUCT_REQUIREMENTS]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:lennys-product-requirements-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:lennys-product-requirements-item',
		contentBlueprintId: '245dd102-5d0f-41a2-b26c-048d43b60005',
	},
	[BLUEPRINT_NAMES.MARKETING_PLAN]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:marketing-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:marketing-item',
		contentBlueprintId: '857e112e-50f1-4a24-b4f5-d6a717e4e279',
	},
	[BLUEPRINT_NAMES.MEETING_NOTES]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:meeting-notes-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:meeting-notes-item',
		contentBlueprintId: 'ec8ecc7e-7e4b-4f8a-aa3f-a6a7f2df4ac9',
	},
	[BLUEPRINT_NAMES.MESSAGE_HOUSE]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:message-house-template-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:message-house-template-item',
		contentBlueprintId: '43864f05-5531-44c1-b54b-66b97993fc9f',
	},
	[BLUEPRINT_NAMES.OKRS]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:okr-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:okr-item',
		contentBlueprintId: '1e2fb2f7-e6d6-4825-8f1e-47033a15bd52',
	},
	[BLUEPRINT_NAMES.PERSONA]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:persona-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:persona-item',
		contentBlueprintId: '181f2ea7-5fd6-4cbc-aa8f-d314f62c2483',
	},
	[BLUEPRINT_NAMES.PRESENTER_MODE]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:presenter-mode-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:presenter-mode-item',
		contentBlueprintId: 'b414482f-1bd4-460e-bf14-8607879a93cb',
	},
	[BLUEPRINT_NAMES.PRODUCT_REQUIREMENTS]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-software-blueprints:requirements-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-software-blueprints:requirements-item',
		contentBlueprintId: '4443a61a-9a9a-4712-b81e-a84e3ae51554',
	},
	[BLUEPRINT_NAMES.PRODUCT_ROADMAP]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:product-roadmap-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:product-roadmap-item',
		contentBlueprintId: '72f7004a-2f37-4ea4-a95f-2daade784d58',
	},
	[BLUEPRINT_NAMES.PROJECT_KICKOFF]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:project-kickoff-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:project-kickoff-item',
		contentBlueprintId: '4342057b-f7b0-4385-9317-2b8de25ee952',
	},
	[BLUEPRINT_NAMES.PROJECT_PLAN]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:projectplan-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:projectplan-item',
		contentBlueprintId: 'f7a9afa1-19c0-4558-abd4-f8060829f9fc',
	},
	[BLUEPRINT_NAMES.PROJECT_STATUS]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:project-status-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:project-status-item',
		contentBlueprintId: 'a2484be7-df58-4f17-a347-2fecb7b22bb9',
	},
	[BLUEPRINT_NAMES.PROJECT_TIMELINE_TEMPLATE]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:project-timeline-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:project-timeline-item',
		contentBlueprintId: 'ecd39da6-2163-4ea4-b6c7-280b7662681b',
	},
	[BLUEPRINT_NAMES.QTC_ACQUISITION]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:qtc-acquisition-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:qtc-acquisition-item',
		contentBlueprintId: 'feafa3b9-bf33-46af-9452-1fbcbe36e0a2',
	},
	[BLUEPRINT_NAMES.REMOTE_TEAM_MEETING]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:remote-team-meeting-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:remote-team-meeting-item',
		contentBlueprintId: '5a273376-0251-49ca-9ee8-197e643bdfd3',
	},
	[BLUEPRINT_NAMES.RISK_ASSESSMENT_MATRIX]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:risk-assessment-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:risk-assessment-item',
		contentBlueprintId: '7f67b5e5-fd5e-4517-9006-0775f3f2f8ec',
	},
	[BLUEPRINT_NAMES.ROLES_AND_RESPONSIBILITIES]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:roles-responsibilities-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:roles-responsibilities-item',
		contentBlueprintId: 'be29e5eb-fbe7-4213-b16f-5fb25b13f2d8',
	},
	[BLUEPRINT_NAMES.SMART_GOALS]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:smart-goals-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:smart-goals-item',
		contentBlueprintId: 'e97d646a-8b86-4561-9daf-ae7ee2de4a19',
	},
	[BLUEPRINT_NAMES.SWOT_ANALYSIS]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:swot-analysis-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:swot-analysis-item',
		contentBlueprintId: '2f7a20e6-4b0d-478f-96cf-1f093470f571',
	},
	[BLUEPRINT_NAMES.SALES_PORTAL]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:sales-portal-template-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:sales-portal-template-item',
		contentBlueprintId: '985c2d47-6f30-4a58-915a-3103786a9f8f',
	},
	[BLUEPRINT_NAMES.SALES_ACCOUNT_PLANNING]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:account-planning-for-sales-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:account-planning-for-sales-item',
		contentBlueprintId: 'cd9f6877-6926-4f48-b178-0d897b70b99c',
	},
	[BLUEPRINT_NAMES.SOFTWARE_ARCHITECTURE_REVIEW]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:software-architecture-review-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:software-architecture-review-item',
		contentBlueprintId: 'd1e47ac0-cc06-4699-a939-b012a4ead2f8',
	},
	[BLUEPRINT_NAMES.SPRINT_PLANNING_MEETING]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:sprint-planning-meeting-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:sprint-planning-meeting-item',
		contentBlueprintId: '611f84bb-1b6e-4a35-ba13-b67fdbeebfb6',
	},
	[BLUEPRINT_NAMES.STAKEHOLDER_COMMUNICATIONS]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:stakeholder-communications-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:stakeholder-communications-item',
		contentBlueprintId: 'b6c3445f-bbc5-4de3-8b06-919619d9a82f',
	},
	[BLUEPRINT_NAMES.STANDARD_OPERATING_PROCEDURE_TEMPLATE]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:standard-operating-procedure-sop-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:standard-operating-procedure-sop-item',
		contentBlueprintId: '14d6eb8f-cac0-494c-a22d-0e41bd76e9f9',
	},
	[BLUEPRINT_NAMES.STRATEGIC_PLAN]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:business-strategy-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:business-strategy-item',
		contentBlueprintId: '43da1e9b-652d-4a0c-9570-cfdcbfc38c10',
	},
	[BLUEPRINT_NAMES.TEAM_HOMEPAGE]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:team-homepage-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:team-homepage-item',
		contentBlueprintId: 'c9160e85-fb5c-4502-a6b9-c39c3f8f9d2a',
	},
	[BLUEPRINT_NAMES.TEAM_POSTER]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:team-poster-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:team-poster-item',
		contentBlueprintId: '2e0ddf37-d490-4bc8-9476-0597d928fe06',
	},
	[BLUEPRINT_NAMES.TO_DO_LIST]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:to-do-list-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:to-do-list-item',
		contentBlueprintId: 'c5ad620d-c78d-4d72-84aa-adf07798dc81',
	},
	[BLUEPRINT_NAMES.TROUBLESHOOTING_ARTICLE]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-knowledge-base:kb-troubleshooting-article-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-knowledge-base:kb-troubleshooting-item',
		contentBlueprintId: 'e864ea83-270e-47de-b573-b21d1cc537cd',
	},
	[BLUEPRINT_NAMES.VENDOR_AGREEMENT]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:vendor-agreement-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:vendor-agreement-item',
		contentBlueprintId: '9f11fe97-3ee7-43d7-8ad0-05253c80c12e',
	},
	[BLUEPRINT_NAMES.VIRTUAL_EVENT]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:virtual-event-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:virtual-event-item',
		contentBlueprintId: '65012d82-c253-41de-8875-552cb92baa1d',
	},
	[BLUEPRINT_NAMES.VISION_TO_VALUES]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:vision-to-values-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:vision-to-values-item',
		contentBlueprintId: '00361a2d-c2c9-4670-9cfc-49d8c4126122',
	},
	[BLUEPRINT_NAMES.END_OF_WEEK_STATUS_REPORT]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:eow-status-report-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:eow-status-report-item',
		contentBlueprintId: '6873d89c-765f-4677-b370-048005c7470b',
	},
	[BLUEPRINT_NAMES.WEEKLY_MEETING_NOTES]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:recurring-meeting-minutes-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:recurring-meeting-minutes-item',
		contentBlueprintId: '7c19a101-d13e-4d67-870b-279e6342c6bc',
	},
	[BLUEPRINT_NAMES.WORK_TRIP_PLANNER]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:work-trip-planner-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:work-trip-planner-item',
		contentBlueprintId: 'b3c70113-a13a-4419-b906-7cac93bcd092',
	},
	[BLUEPRINT_NAMES.WORKSHOP_PLANNER]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:workshop-planner-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:workshop-planner-item',
		contentBlueprintId: 'e9889133-019b-4283-a4d2-9f916081cc76',
	},
	[BLUEPRINT_NAMES.WRITING_GUIDELINES]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:writing-guideline-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:writing-guideline-item',
		contentBlueprintId: '0193f684-d07b-4af1-af11-7dc20d7d7bc4',
	},
	[BLUEPRINT_NAMES.QUARTERLY_CHECK_IN]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:quarterly-check-in-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:quarterly-check-in-item',
		contentBlueprintId: 'e2a1cea6-c5e7-4de1-b397-862efddc17d8',
	},
	[BLUEPRINT_NAMES.AWS_ARCHITECTURE_DIAGRAM]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:aws-architecture-diagram-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:aws-architecture-diagram-item',
		contentBlueprintId: '777e5e10-8151-411b-9d3a-e18c32e1ccf9',
	},
	[BLUEPRINT_NAMES.MARKETING_BLOG_POST]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:marketing-blog-post-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:marketing-blog-post-item',
		contentBlueprintId: '77b5e06c-c8a6-4fa5-b104-69b4b2c3006d',
	},
	[BLUEPRINT_NAMES.MARKETING_CAMPAIGN]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:marketing-campaign-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:marketing-campaign-item',
		contentBlueprintId: '4254b414-5ba3-4178-85fd-03c93166d92b',
	},
	[BLUEPRINT_NAMES.HIRING_PROCESS]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:hiring-process-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:hiring-process-item',
		contentBlueprintId: '29e1a62b-919d-4a1d-9325-ab26e1539bba',
	},
	[BLUEPRINT_NAMES.MUTUAL_ACTION_PLAN]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:mutual-action-plan-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:mutual-action-plan-item',
		contentBlueprintId: 'cd7d12fc-79c5-45fc-97e0-275b5fe09063',
	},
	[BLUEPRINT_NAMES.ITSM_WEEKLY_MAJOR_INCIDENT_REPORT]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:itsm-weekly-major-incident-report-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:itsm-weekly-major-incident-report-item',
		contentBlueprintId: '00917469-8d47-4375-8148-8f6e42db13a6',
	},
	[BLUEPRINT_NAMES.DISRUPTIVE_BRAINSTORMING]: {
		blueprintModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:disrupt-brainstorming-blueprint',
		itemModuleCompleteKey:
			'com.atlassian.confluence.plugins.confluence-business-blueprints:disrupt-brainstorming-item',
		contentBlueprintId: '01e2cb00-2daf-41d8-aec2-e409c985bcbb',
	},
} as const;

export const mapBlueprintModuleKeyToTemplate = (
	templates?: PersonalizedTemplatesV2AllTemplatesQuery,
): Record<string, TemplateInfoType> => {
	const blueprintModuleKeyToTemplate: Record<string, TemplateInfoType> = {};
	templates?.allTemplates?.nodes?.forEach((template: TemplateInfoType | null) => {
		if (template?.blueprintModuleCompleteKey) {
			blueprintModuleKeyToTemplate[template.blueprintModuleCompleteKey!] = template;
		}
	});
	return blueprintModuleKeyToTemplate;
};
